import { getStatusLabel } from '../../../status-labels';
import type { BillStatus } from '../../../types/bills';
import { definePolicy, type Policy } from '../kernel';
import { allow, deny } from '../types';
import { GROWER_PRODUCT_EXPENSES_EDITABLE_STATUSES, type GrowerProductStatus } from './growerProduct';
import {
  ORDER_CHARGE_BILL_LOCKED_MESSAGE,
  ORDER_EXPENSE_BILL_LOCKED,
  orderChargeBillLock,
} from './orderChargeBillLock';

/**
 * GROWER PRODUCT CHARGES — field-blind, like the charges grid's default lock.
 *
 *   status ∈ DRAFT..CLOSED           ─✗─▶ GROWER_PRODUCT_CHARGES_LOCKED
 *   own + child bills DRAFT/CANCELLED ─✗─▶ ORDER_EXPENSE_BILL_LOCKED
 *   repair incomplete allocation      ──▶ always allowed
 */
export type GrowerProductExpenseFacts = {
  growerProductStatus: GrowerProductStatus;
  billStatuses: readonly BillStatus[];
};

export const GROWER_PRODUCT_EXPENSE_FIELDS = [
  'expenseType',
  'accountLevel',
  'ledgerAccount',
  'businessEntity',
  'chargePer',
  'quantity',
  'unitAmount',
  'includedInDlvPrice',
  'description',
  'linkedLineItems',
] as const;

export type GrowerProductExpenseField = (typeof GROWER_PRODUCT_EXPENSE_FIELDS)[number];

export const GROWER_PRODUCT_EXPENSE_OPS = ['clear', 'duplicate', 'repair incomplete allocation'] as const;
export type GrowerProductExpenseOp = (typeof GROWER_PRODUCT_EXPENSE_OPS)[number];

/** The charges grid's sentence for a bill-locked row: the one every order charge shares. */
export const GROWER_PRODUCT_EXPENSE_BILL_LOCKED_MESSAGE = ORDER_CHARGE_BILL_LOCKED_MESSAGE;

const fieldRule = () => allow();

const fieldRules = {
  expenseType: fieldRule,
  accountLevel: fieldRule,
  ledgerAccount: fieldRule,
  businessEntity: fieldRule,
  chargePer: fieldRule,
  quantity: fieldRule,
  unitAmount: fieldRule,
  includedInDlvPrice: fieldRule,
  description: fieldRule,
  linkedLineItems: fieldRule,
} satisfies Record<GrowerProductExpenseField, typeof fieldRule>;

export const growerProductExpensePolicy: Policy<
  GrowerProductExpenseFacts,
  GrowerProductExpenseField,
  GrowerProductExpenseOp
> = definePolicy<GrowerProductExpenseFacts, GrowerProductExpenseField, GrowerProductExpenseOp>({
  kind: 'grower product charge',
  fields: GROWER_PRODUCT_EXPENSE_FIELDS,
  ops: GROWER_PRODUCT_EXPENSE_OPS,
  requires: {},
  entity: (facts, action) => {
    if (action.type === 'view') return allow();
    if (action.type === 'perform' && action.op === 'repair incomplete allocation') return allow();

    if (!GROWER_PRODUCT_EXPENSES_EDITABLE_STATUSES.includes(facts.growerProductStatus)) {
      return deny(
        'GROWER_PRODUCT_CHARGES_LOCKED',
        `Charges are locked once the grower product is ${getStatusLabel(facts.growerProductStatus)}.`
      );
    }

    return action.type === 'add'
      ? allow()
      : orderChargeBillLock(facts.billStatuses, ORDER_EXPENSE_BILL_LOCKED, GROWER_PRODUCT_EXPENSE_BILL_LOCKED_MESSAGE);
  },
  field: fieldRules,
});
